/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import crystalAirLogoDark from '@/assets/images/Crystal-Air_Logo_Black-Blue.svg';
import crystalAirLogoLight from '@/assets/images/Crystal-Air_Logo_White-Blue.svg';
import planeAnimation from '@/assets/images/plane-animation.png';
import charterBg from '@/assets/images/charter-bg.jpg';
import learnToFlyBg from '@/assets/images/learn-to-fly-bg.jpg';
import freedomIcon from '@/assets/images/freedom-icon.svg';
import lifestyleIcon from '@/assets/images/lifestyle-icon.svg';
import optionsIcon from '@/assets/images/options-icon.svg';
import communityIcon from '@/assets/images/community-icon.svg';
import airplaneInterior from '@/assets/images/airplane-interior.jpg';
import flightInstructionBg from '@/assets/images/flight-instruction-bg.jpg';
import charterDiscoverBg from '@/assets/images/charter-discover-bg.jpg';
import aircraftManagementBg from '@/assets/images/aircraft-management-bg.jpg';
import maintenanceBg from '@/assets/images/maintenance-bg.jpg';
import instagramIcon from '@/assets/images/instagram-icon.svg';
import facebookIcon from '@/assets/images/facebook-icon.svg';
import tiktokIcon from '@/assets/images/tiktok-icon.svg';
import footerLogo from '@/assets/images/footer-logo.svg';

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Crystal Air logos
  "/images/Crystal-Air_Logo_Black-Blue.svg": crystalAirLogoDark,
  "/images/Crystal-Air_Logo_White-Blue.svg": crystalAirLogoLight,
  "/assets/images/Crystal-Air_Logo_Black-Blue.svg": crystalAirLogoDark,
  "/assets/images/Crystal-Air_Logo_White-Blue.svg": crystalAirLogoLight,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7923_Crystal-Air_Logo_Black-Blue.svg": crystalAirLogoDark,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7925_Crystal-Air_Logo_White-Blue.svg": crystalAirLogoLight,

  // Plane animation for Dream section
  "/images/plane-animation.png": planeAnimation,
  "/assets/images/plane-animation.png": planeAnimation,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc79a9_Main0012.png": planeAnimation,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc79a9_Main0012-p-1600.png": planeAnimation,

  // Hero video assets (served from public directory)
  "/videos/hero-video.mp4": "/videos/hero-video.mp4",
  "/videos/hero-video.webm": "/videos/hero-video.webm",
  "/videos/hero-poster.jpg": "/videos/hero-poster.jpg",
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc79ac_20230830_WebBroll%20Homepage_v5-transcode.mp4": "/videos/hero-video.mp4",
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc79ac_20230830_WebBroll%20Homepage_v5-transcode.webm": "/videos/hero-video.webm",
  "https://cdn.prod.website-files.com/64d5132a71edf8c5ab3c4da8/64fa128b4b5d9b8cc455d147_20230830_WebBroll%20Homepage_v5-poster-00001.jpg": "/videos/hero-poster.jpg",

  // Choose Your Path background images
  "/images/charter-bg.jpg": charterBg,
  "/assets/images/charter-bg.jpg": charterBg,
  "/images/learn-to-fly-bg.jpg": learnToFlyBg,
  "/assets/images/learn-to-fly-bg.jpg": learnToFlyBg,

  // Experience section icons
  "/images/freedom-icon.svg": freedomIcon,
  "/assets/images/freedom-icon.svg": freedomIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7908_Vectors-Wrapper.svg": freedomIcon,
  "/images/lifestyle-icon.svg": lifestyleIcon,
  "/assets/images/lifestyle-icon.svg": lifestyleIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7909_Vectors-Wrapper.svg": lifestyleIcon,
  "/images/options-icon.svg": optionsIcon,
  "/assets/images/options-icon.svg": optionsIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc790a_Vectors-Wrapper.svg": optionsIcon,
  "/images/community-icon.svg": communityIcon,
  "/assets/images/community-icon.svg": communityIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc790b_Vectors-Wrapper.svg": communityIcon,

  // Experience section airplane interior image
  "/images/airplane-interior.jpg": airplaneInterior,
  "/assets/images/airplane-interior.jpg": airplaneInterior,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc796f_2023.08.21_CA-ContentDay_TV-1602.jpg": airplaneInterior,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc796f_2023.08.21_CA-ContentDay_TV-1602-p-1600.jpg": airplaneInterior,

  // Discover section service images
  "/images/flight-instruction-bg.jpg": flightInstructionBg,
  "/assets/images/flight-instruction-bg.jpg": flightInstructionBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7971_2023.08.21_CrystalAir_TV-1049.jpg": flightInstructionBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7971_2023.08.21_CrystalAir_TV-1049-p-500.jpg": flightInstructionBg,
  "/images/charter-discover-bg.jpg": charterDiscoverBg,
  "/assets/images/charter-discover-bg.jpg": charterDiscoverBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7972_2023.08.21_CA-ContentDay_TV-1950.jpg": charterDiscoverBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7972_2023.08.21_CA-ContentDay_TV-1950-p-500.jpg": charterDiscoverBg,
  "/images/aircraft-management-bg.jpg": aircraftManagementBg,
  "/assets/images/aircraft-management-bg.jpg": aircraftManagementBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7916_2022.12.04.CrystalAir_MR-5231.jpg": aircraftManagementBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7916_2022.12.04.CrystalAir_MR-5231-p-500.jpg": aircraftManagementBg,
  "/images/maintenance-bg.jpg": maintenanceBg,
  "/assets/images/maintenance-bg.jpg": maintenanceBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7989_2023.08.21_CrystalAir_TV-0269.jpg": maintenanceBg,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7989_2023.08.21_CrystalAir_TV-0269-p-500.jpg": maintenanceBg,

  // Footer and social media icons
  "/images/instagram-icon.svg": instagramIcon,
  "/assets/images/instagram-icon.svg": instagramIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7940_Instagram_Icon.svg": instagramIcon,
  "/images/facebook-icon.svg": facebookIcon,
  "/assets/images/facebook-icon.svg": facebookIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7910_Vectors-Wrapper.svg": facebookIcon,
  "/images/tiktok-icon.svg": tiktokIcon,
  "/assets/images/tiktok-icon.svg": tiktokIcon,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc7911_Vectors-Wrapper.svg": tiktokIcon,
  "/images/footer-logo.svg": footerLogo,
  "/assets/images/footer-logo.svg": footerLogo,
  "https://cdn.prod.website-files.com/64fa264c1f05b9ea79dc792b/64fa264c1f05b9ea79dc790e_Vectors-Wrapper.svg": footerLogo,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
